import { supabase } from '../lib/supabase';

export const appointmentService = {
  /**
   * Get all appointments (with tenant filtering)
   */
  async getAll(tenantId = null, isSuperAdmin = false) {
    try {
      let query = supabase?.from('appointments')?.select(`
          *,
          customer:customers(id, company_name, contact_person, email, phone),
          service:services(id, name, duration, price),
          assigned_user:user_profiles!assigned_to(id, first_name, last_name, email),
          created_by_user:user_profiles!created_by(id, first_name, last_name, email)
        `)?.order('start_time', { ascending: true });

      // Apply tenant filter ONLY for non-Super Admins
      // Super Admins see ALL appointments across ALL tenants
      if (!isSuperAdmin && tenantId) {
        query = query?.eq('tenant_id', tenantId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(apt => ({
        id: apt?.id,
        tenantId: apt?.tenant_id,
        title: apt?.title,
        description: apt?.description,
        customerId: apt?.contact_id,
        serviceId: apt?.service_id,
        assignedTo: apt?.assigned_to,
        startTime: apt?.start_time,
        endTime: apt?.end_time,
        status: apt?.status,
        recurrence: apt?.recurrence,
        recurrenceEndDate: apt?.recurrence_end_date,
        notes: apt?.notes,
        location: apt?.location,
        customer: apt?.customer,
        service: apt?.service,
        assignedUser: apt?.assigned_user,
        createdAt: apt?.created_at,
        updatedAt: apt?.updated_at
      }));
    } catch (error) {
      console.error('Get appointments error:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('appointments')?.select(`
          *,
          customer:customers(id, company_name, contact_person, email, phone),
          service:services(id, name, duration, price),
          assigned_staff:user_profiles!appointments_assigned_to_fkey(id, first_name, last_name, email)
        `)?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        title: data?.title,
        description: data?.description,
        customerId: data?.contact_id,
        customer: data?.customer,
        serviceId: data?.service_id,
        service: data?.service,
        assignedTo: data?.assigned_to,
        assignedStaff: data?.assigned_staff,
        startTime: data?.start_time,
        endTime: data?.end_time,
        status: data?.status,
        recurrence: data?.recurrence,
        recurrenceEndDate: data?.recurrence_end_date,
        notes: data?.notes,
        location: data?.location,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching appointment:', error);
      throw error;
    }
  },

  async create(appointment) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('appointments')?.insert({
          title: appointment?.title,
          description: appointment?.description,
          contact_id: appointment?.customerId,
          service_id: appointment?.serviceId,
          assigned_to: appointment?.assignedTo,
          start_time: appointment?.startTime,
          end_time: appointment?.endTime,
          status: appointment?.status || 'scheduled',
          recurrence: appointment?.recurrence || 'none',
          recurrence_end_date: appointment?.recurrenceEndDate,
          notes: appointment?.notes,
          location: appointment?.location,
          tenant_id: appointment?.tenantId,
          created_by: user?.id
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        title: data?.title,
        description: data?.description,
        customerId: data?.contact_id,
        serviceId: data?.service_id,
        assignedTo: data?.assigned_to,
        startTime: data?.start_time,
        endTime: data?.end_time,
        status: data?.status,
        recurrence: data?.recurrence,
        recurrenceEndDate: data?.recurrence_end_date,
        notes: data?.notes,
        location: data?.location,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  },

  async update(id, updates) {
    try {
      const { data, error } = await supabase?.from('appointments')?.update({
          title: updates?.title,
          description: updates?.description,
          contact_id: updates?.customerId,
          service_id: updates?.serviceId,
          assigned_to: updates?.assignedTo,
          start_time: updates?.startTime,
          end_time: updates?.endTime,
          status: updates?.status,
          recurrence: updates?.recurrence,
          recurrence_end_date: updates?.recurrenceEndDate,
          notes: updates?.notes,
          location: updates?.location
        })?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        title: data?.title,
        description: data?.description,
        customerId: data?.contact_id,
        serviceId: data?.service_id,
        assignedTo: data?.assigned_to,
        startTime: data?.start_time,
        endTime: data?.end_time,
        status: data?.status,
        recurrence: data?.recurrence,
        recurrenceEndDate: data?.recurrence_end_date,
        notes: data?.notes,
        location: data?.location,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('appointments')?.delete()?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting appointment:', error);
      throw error;
    }
  },

  async updateStatus(id, status) {
    try {
      const { data, error } = await supabase?.from('appointments')?.update({ status })?.eq('id', id)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating appointment status:', error);
      throw error;
    }
  },

  async checkConflicts(startTime, endTime, assignedTo, excludeId = null) {
    try {
      let query = supabase?.from('appointments')?.select('id, title, start_time, end_time')?.eq('assigned_to', assignedTo)?.neq('status', 'cancelled')?.or(`and(start_time.lte.${endTime},end_time.gte.${startTime})`);

      if (excludeId) {
        query = query?.neq('id', excludeId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data?.length > 0 ? data : null;
    } catch (error) {
      console.error('Error checking conflicts:', error);
      throw error;
    }
  }
};